import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface TenantUserDetail {
  id: string;
  tenantId: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    externalAuthId?: string;
  };
}

interface PlatformTenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  counts?: {
    users?: number;
    counterparties?: number;
    assets?: number;
  };
}

export default function PlatformTenantsPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<Record<string, TenantUserDetail[]>>({});
  const [usersLoading, setUsersLoading] = useState<Record<string, boolean>>({});

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const expandedBg = isDark ? 'rgba(255,255,255,0.02)' : '#FAFAFA';
  const userCardBg = isDark ? '#1A1A1A' : '#fff';
  const userCardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';

  useEffect(() => { loadTenants(); }, []);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const data = await api.get<PlatformTenant[]>('/admin/tenants');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setItems([]);
      else addToast(getApiErrorMessage(err, 'Failed to load tenants'), 'error');
    } finally { setLoading(false); }
  };

  const loadTenantUsers = async (tenantId: string) => {
    if (tenantUsers[tenantId]) return;
    setUsersLoading((prev) => ({ ...prev, [tenantId]: true }));
    try {
      const data = await api.get<TenantUserDetail[]>(`/tenants/${tenantId}/users`);
      setTenantUsers((prev) => ({ ...prev, [tenantId]: data }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setTenantUsers((prev) => ({ ...prev, [tenantId]: [] }));
      else addToast(getApiErrorMessage(err, 'Failed to load tenant users'), 'error');
    } finally { setUsersLoading((prev) => ({ ...prev, [tenantId]: false })); }
  };

  const toggleExpand = (tenantId: string) => {
    if (expandedId === tenantId) { setExpandedId(null); }
    else { setExpandedId(tenantId); loadTenantUsers(tenantId); }
  };

  const filtered = items.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase()));

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      suspended: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
    };
    const style = map[status] ?? map.inactive;
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>{status}</span>;
  };

  return (
    <DashboardLayout breadcrumb={['Platform Admin', 'Tenants Overview']}>
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>Tenants Overview</h1><p className="text-sm mt-1" style={{ color: textMuted }}>System-wide view of all platform tenants</p></div>
      </div>
      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or slug..." className="bg-transparent outline-none text-sm flex-1" style={{ color: textMain }} />
          </div>
          <button onClick={loadTenants} className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors" style={{ border: `1px solid ${inputBorder}`, background: selectBg }} title="Refresh">
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} style={{ color: textMuted }}></i>
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center"><i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i><p className="text-sm mt-3" style={{ color: textMuted }}>Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}><i className="ri-server-line text-xl" style={{ color: textMuted }}></i></div><p className="text-sm font-medium" style={{ color: textMain }}>No tenants</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr style={{ background: tableHeaderBg }}>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Name</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Slug</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Status</th><th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Users</th><th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>CP</th><th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Assets</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Created</th><th className="px-4 py-3 w-10"></th></tr></thead>
            <tbody>
              {filtered.map((tenant) => {
                const isExpanded = expandedId === tenant.id;
                const isUsersLoading = usersLoading[tenant.id];
                const users = tenantUsers[tenant.id] ?? [];
                return (
                  <><tr key={tenant.id} className="cursor-pointer transition-colors" onClick={() => toggleExpand(tenant.id)} style={{ borderBottom: `1px solid ${tableBorder}` }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <td className="px-4 py-3"><div className="font-medium" style={{ color: textMain }}>{tenant.name}</div></td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: textMuted }}>{tenant.slug}</td>
                    <td className="px-4 py-3">{getStatusBadge(tenant.status)}</td>
                    <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: textMain }}>{tenant.counts?.users ?? 0}</td>
                    <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: textMain }}>{tenant.counts?.counterparties ?? 0}</td>
                    <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: textMain }}>{tenant.counts?.assets ?? 0}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>{new Date(tenant.createdAt).toLocaleDateString('uk-UA')}</td>
                    <td className="px-4 py-3"><button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors"
                      onMouseEnter={(e) => { e.currentTarget.style.background = iconBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      <i className={`ri-arrow-down-s-line transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: textMuted }}></i></button></td>
                  </tr>
                  {isExpanded && (
                    <tr><td colSpan={8} className="px-0 py-0">
                      <div className="px-4 py-4" style={{ background: expandedBg }}>
                        <div className="mb-3"><span className="text-xs font-semibold uppercase tracking-wider" style={{ color: textMuted }}>Tenant ID</span><div className="mt-1 font-mono text-xs" style={{ color: textMain }}>{tenant.id}</div></div>
                        {isUsersLoading ? (
                          <div className="flex items-center gap-2 text-sm" style={{ color: textMuted }}><i className="ri-loader-4-line animate-spin"></i>Loading users...</div>
                        ) : users.length === 0 ? (
                          <p className="text-sm" style={{ color: textMuted }}>No users in this tenant</p>
                        ) : (
                          <div><span className="text-xs font-semibold uppercase tracking-wider" style={{ color: textMuted }}>Users ({users.length})</span>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {users.map((tu) => (
                                <div key={tu.id} className="rounded-lg p-3 flex items-center gap-3" style={{ background: userCardBg, border: `1px solid ${userCardBorder}` }}>
                                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#D12229' }}>{(tu.user.fullName || '?').slice(0, 1).toUpperCase()}</div>
                                  <div className="min-w-0"><div className="text-sm font-medium truncate" style={{ color: textMain }}>{tu.user.fullName}</div><div className="text-xs truncate" style={{ color: textMuted }}>{tu.user.email}</div><div className="text-[11px] font-medium mt-0.5 capitalize" style={{ color: '#1F5B8F' }}>{tu.role}</div></div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td></tr>
                  )}
                  </>
                );
              })}
            </tbody></table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}